import * as signalR from '@microsoft/signalr';
import { useAuthStore } from '@/stores/authStore';

const HUB_URL = import.meta.env.VITE_SIGNALR_URL
  ? `${import.meta.env.VITE_SIGNALR_URL}/hubs/notifications`
  : '/hubs/notifications';

// Logger utility - only logs in development mode
const logger = {
  info: (message: string, ...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.info(`[SignalR] ${message}`, ...args);
    }
  },
  warn: (message: string, ...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.warn(`[SignalR] ${message}`, ...args);
    }
  },
  error: (message: string, ...args: unknown[]) => {
    // Always log errors
    console.error(`[SignalR] ${message}`, ...args);
  },
};

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private reconnectAttempts = 0;
  private listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map();
  private connectionChangeListeners: Set<(connected: boolean) => void> = new Set();
  // Serializes every start()/stop() call so overlapping calls (one React effect re-run per
  // silent token refresh, roughly every 10-20 min) can never race each other. Without this, a
  // start() still mid-handshake when another start()/stop() ran could finish afterwards and
  // either (a) become an orphaned "zombie" connection nobody ever stops - still joined to the
  // tenant's SignalR group, so it goes on independently receiving every broadcast forever - or
  // (b) have stop()'s `this.connection = null` land AFTER a newer start() had already taken
  // over, nulling out the pointer to the live connection instead of the dead one. Both were
  // observed in production: a single low-stock event produced 4 identical alerts from 4
  // accumulated zombie connections built up over one session.
  private operationQueue: Promise<void> = Promise.resolve();

  private enqueue(operation: () => Promise<void>): Promise<void> {
    const run = this.operationQueue.then(operation, operation);
    // However this operation turns out, the NEXT one must still run - only the caller of this
    // particular operation needs to see its real outcome (returned via `run` below).
    this.operationQueue = run.catch(() => undefined);
    return run;
  }

  /** Subscribe to connected/disconnected transitions (initial connect, reconnect, close). */
  onConnectionChange(callback: (connected: boolean) => void): () => void {
    this.connectionChangeListeners.add(callback);
    return () => {
      this.connectionChangeListeners.delete(callback);
    };
  }

  private notifyConnectionChange(connected: boolean): void {
    this.connectionChangeListeners.forEach((cb) => cb(connected));
  }

  async start(accessToken: string): Promise<void> {
    return this.enqueue(() => this.startInternal(accessToken));
  }

  private async startInternal(accessToken: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      logger.info('Already connected');
      return;
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        // Read the current token from the store on every call, not the token string this
        // start() call happened to close over - withAutomaticReconnect re-invokes this same
        // factory on every reconnect attempt, so a token refreshed mid-outage was never picked
        // up and reconnection would keep failing auth with the original (possibly stale) token.
        accessTokenFactory: () => useAuthStore.getState().token ?? accessToken,
        withCredentials: true,
      })
      .withAutomaticReconnect({
        // Never give up. This used to return null (stop) after 5 attempts - a ~31s budget
        // (1+2+4+8+16s) that a dev-server restart, a docker rebuild, or a laptop going to sleep
        // easily outlasts. Once retries stopped, onclose set the connection to null and nothing
        // ever tried again until a full page reload - live ticket/status pushes went silently
        // dead and the ticket list only ever caught up on the next manual refresh. Backing off up
        // to a 30s ceiling and retrying forever means the tab recovers on its own once the server
        // is back, with no risk of hammering it (worst case one attempt every 30s indefinitely).
        nextRetryDelayInMilliseconds: (retryContext) =>
          Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000),
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    // Set up reconnection handling
    this.connection.onreconnecting((error) => {
      logger.warn('Reconnecting...', error);
      this.reconnectAttempts++;
      this.notifyConnectionChange(false);
    });

    this.connection.onreconnected((connectionId) => {
      logger.info('Reconnected:', connectionId);
      this.reconnectAttempts = 0;
      // Re-register all listeners after reconnection
      this.reRegisterListeners();
      this.notifyConnectionChange(true);
    });

    this.connection.onclose((error) => {
      logger.warn('Connection closed:', error);
      this.connection = null;
      this.notifyConnectionChange(false);
    });

    try {
      await this.connection.start();
      logger.info('Connected successfully');
      this.reconnectAttempts = 0;
      // Attach any listeners that were registered via on() before this connection existed
      // (this.connection was null at the time, so on() could only store them, not attach them).
      this.reRegisterListeners();
      this.notifyConnectionChange(true);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes('stopped during negotiation') || msg.includes('AbortError')) {
        return; // Connection was intentionally stopped (React cleanup / strict mode)
      }
      logger.error('Connection failed:', error);
      throw error;
    }
  }

  async stop(): Promise<void> {
    return this.enqueue(() => this.stopInternal());
  }

  private async stopInternal(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.notifyConnectionChange(false);
    }
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  // Subscribe to events
  on<T>(event: string, callback: (data: T) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    const callbacks = this.listeners.get(event)!;
    callbacks.add(callback as (...args: unknown[]) => void);

    // Register with SignalR if connected
    if (this.connection) {
      this.connection.on(event, callback);
    }

    // Return unsubscribe function
    return () => {
      callbacks.delete(callback as (...args: unknown[]) => void);
      if (this.connection) {
        this.connection.off(event, callback);
      }
    };
  }

  // Invoke hub methods
  async invoke<T>(method: string, ...args: unknown[]): Promise<T | undefined> {
    if (!this.connection || !this.isConnected()) {
      // Subscribe/unsubscribe calls race the connection's own state on every reconnect: the
      // callers below check isConnected before calling, but that's a React state snapshot that
      // can already be stale by the time this async call actually runs (a stop()/start() cycle -
      // e.g. MainLayout reconnecting on every token refresh - can land in between). This used to
      // throw, which nobody awaiting these fire-and-forget calls caught, so it surfaced as an
      // "Uncaught (in promise)" error on every such race - console noise, not a real failure, since
      // whichever hook made this call re-subscribes on its own the next time isConnected flips true.
      logger.warn(`Skipped ${method} - not connected`);
      return undefined;
    }
    return await this.connection.invoke<T>(method, ...args);
  }

  // Subscribe to ticket updates
  async subscribeToTicket(ticketId: string): Promise<void> {
    await this.invoke('SubscribeToTicket', ticketId);
  }

  async unsubscribeFromTicket(ticketId: string): Promise<void> {
    await this.invoke('UnsubscribeFromTicket', ticketId);
  }

  // Subscribe to dashboard updates
  async subscribeToDashboard(): Promise<void> {
    await this.invoke('SubscribeToDashboard');
  }

  async unsubscribeFromDashboard(): Promise<void> {
    await this.invoke('UnsubscribeFromDashboard');
  }

  private reRegisterListeners(): void {
    if (!this.connection) return;

    // off() before on() so this stays idempotent - callbacks that were already attached (e.g.
    // registered while the connection was mid-handshake) don't end up firing twice.
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.connection!.off(event, callback);
        this.connection!.on(event, callback);
      });
    });
  }
}

// Singleton instance
export const signalRService = new SignalRService();

// Event types
export interface TicketNotification {
  ticketId: string;
  ticketNumber: string;
  customerName: string;
  deviceName: string;
  status: string;
  technicianName?: string;
  timestamp: string;
}

export interface StatusChangeNotification {
  ticketId: string;
  ticketNumber: string;
  oldStatus: string;
  newStatus: string;
  changedBy?: string;
  timestamp: string;
}

export interface DashboardUpdateNotification {
  type: string;
  totalTickets?: number;
  pendingTickets?: number;
  completedToday?: number;
  revenueToday?: number;
  timestamp: string;
}

export interface UserNotification {
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  actionUrl?: string;
  timestamp: string;
}

export interface LowStockNotification {
  partId: string;
  partName: string;
  sku: string;
  currentStock: number;
  minimumStock: number;
  timestamp: string;
}
